import React, { useState, useEffect, useRef } from 'react';
import { Bell, CheckCheck, X } from 'lucide-react';
import { notificationService, Notification } from '../../services/notificationService';

const TYPE_ICONS: Record<string, string> = {
  new_follower: '👤',
  post_comment: '💬',
  post_like: '❤️',
  post_bookmark: '🔖',
  content_rejected: '⚠️',
  contract_created: '📄',
  contract_status: '📋',
  milestone_submitted: '📦',
  milestone_approved: '✅',
  milestone_rejected: '❌',
  trial_goal_updated: '🎯',
  trial_note_updated: '📝',
  new_bid: '💰',
  new_message: '💬',
  payment: '💳',
};

export const NotificationBell: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Poll unread count every 30 seconds
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const res = await notificationService.getUnreadCount();
        if (res.success && res.data) {
          setUnreadCount(res.data.count);
        }
      } catch {}
    };

    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleMouseEnter = async () => {
    setIsOpen(true);
    setLoading(true);
    try {
      const res = await notificationService.getNotifications(30);
      if (res.success && res.data) {
        setNotifications(res.data.notifications);
        setUnreadCount(res.data.unreadCount);
      }
    } catch {}
    setLoading(false);
  };

  const handleMouseLeave = () => {
    setIsOpen(false);
  };

  const handleMarkRead = async (notificationId: string) => {
    await notificationService.markAsRead(notificationId);
    setNotifications(prev => prev.map(n => n.notificationId === notificationId ? { ...n, isRead: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };


  const handleDelete = async (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation();
    const wasUnread = notifications.find(n => n.notificationId === notificationId && !n.isRead);
    await notificationService.delete(notificationId);
    setNotifications(prev => prev.filter(n => n.notificationId !== notificationId));
    if (wasUnread) setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const handleMarkAllRead = async () => {
    await notificationService.markAllAsRead();
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    setUnreadCount(0);
  };

  const formatTime = (ts: number) => {
    const diff = Date.now() - ts;
    if (diff < 60000) return '刚刚';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} 分钟前`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} 小时前`;
    return `${Math.floor(diff / 86400000)} 天前`;
  };

  return (
    <div 
      className="relative" 
      ref={dropdownRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        className="relative p-2 rounded-full hover:bg-gray-100 transition-colors group"
      >
        <Bell className="w-5 h-5 text-gray-600 group-hover:text-black transition-colors" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 bg-red-500 rounded-full text-[10px] text-white font-medium flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full pt-2 w-80 z-50">
          <div className="bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden">
            {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900 text-sm">通知</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                全部已读
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-[400px] overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-black"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-10 text-center text-gray-400 text-sm">暂无通知</div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.notificationId}
                  onClick={() => {
                    if (!n.isRead) handleMarkRead(n.notificationId);
                  }}
                  className={`px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors cursor-pointer group/item ${!n.isRead ? 'bg-blue-50/40' : ''}`}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-lg mt-0.5">{TYPE_ICONS[n.type] || '🔔'}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-sm ${!n.isRead ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                          {n.title}
                        </p>
                        {!n.isRead && <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></span>}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.body}</p>
                      <p className="text-[10px] text-gray-400 mt-1">{formatTime(n.createdAt)}</p>
                    </div>
                    <button
                      onClick={(e) => handleDelete(e, n.notificationId)}
                      className="p-1 rounded-full opacity-0 group-hover/item:opacity-100 hover:bg-gray-200 transition-all flex-shrink-0"
                    >
                      <X className="w-3.5 h-3.5 text-gray-400" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
          </div>
        </div>
      )}

    </div>
  );
};
